/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { TVisitor } from "apis/visitors/type";
import { reSendGuideMessage } from "apis/visitors/visitor";
import { isValidEmail, isValidPhoneNumber } from "pages/visitors/register/visitor-table";
import { useEffect, useState } from "react";
import Modal from "react-modal";
import { theme } from "styles/theme";
import Button from "uis/Button";
import Icon from "uis/Icon";
import InputField from "uis/InputField";
import Select_V2 from "uis/Select/select_v2";

type Props = {
  modalIsOpen: boolean;
  closeModal: () => void;
  visitor?: TVisitor;
  onHandleSend?: () => void;
  onUpdateVisitorInfo?: () => void;
};

Modal.setAppElement("#root");

const modalStyle = {
  content: {
    width: 524,
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    borderRadius: "15px",
  },
  overlay: {
    backgroundColor: "var(--component-color-material-dimmer)",
    zIndex: 1000,
  },
};

const Title = styled.p`
  font-size: 24px;
  font-weight: 700;
  text-align: center;
`;

const Label = styled.p`
  font-size: 16px;
  font-weight: 700;
  width: 130px;
  border-right: 2px solid ${theme.neutral50};
  padding-bottom: 30px;
`;

const Row = styled.div`
  display: flex;
  gap: 20px;
`;
const LoadingBtn = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px 0;
  background-color: ${theme.neutral50};
  border-radius: 8px;
  color: ${theme.neutral400};
  font-weight: 600;
  flex: 1;
`;

const ReSendMessageModal = (props: Props) => {
  const { modalIsOpen, closeModal, onHandleSend, onUpdateVisitorInfo, visitor } = props;
  const [visitorInfo, setVisitorInfo] = useState<TVisitor | undefined>(visitor);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string | undefined>();

  const isRightContact = () => {
    if (visitorInfo?.guideChannel === "EMAIL") {
      if (isValidEmail(`${visitorInfo.email}`)) return true;
    } else {
      if (isValidPhoneNumber(`${visitorInfo?.phoneNumber}`)) return true;
    }
  };

  if (visitorInfo) {
    return (
      <Modal isOpen={modalIsOpen} onRequestClose={closeModal} style={modalStyle}>
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 40px;
            padding: 50px 30px 30px 30px;
            position: relative;
          `}
        >
          <div
            onClick={closeModal}
            css={css`
              position: absolute;
              top: 27px;
              right: 27px;
            `}
          >
            <Icon name="close" />
          </div>
          <Title>방문자의 정보를 입력해주세요.</Title>
          <div
            css={css`
              display: flex;
              flex-direction: column;
            `}
          >
            <Row>
              <Label>이름</Label>
              <Label
                css={css`
                  border-right: 0;
                `}
              >
                {visitor?.name}
              </Label>
            </Row>
            <Row>
              <Label>안내 채널</Label>
              <Select_V2
                showArrow={true}
                data={["kakao", "email"]}
                onChange={(value) => {
                  setVisitorInfo({ ...visitorInfo, guideChannel: value === "kakao" ? "KAKAO" : "EMAIL" });
                }}
                value={visitorInfo?.guideChannel.toLowerCase()}
                styles={{
                  container: css`
                    width: 100px;
                    background-color: white;
                    padding: 0;
                    &:hover {
                      background-color: white;
                    }
                  `,
                  text: css`
                    font-size: 16px;
                    font-weight: 500;
                  `,
                }}
              />
            </Row>
            <Row>
              <Label>연락처</Label>
              <InputField
                value={
                  visitorInfo?.guideChannel === "EMAIL"
                    ? visitorInfo.email?.toString()
                    : visitorInfo.phoneNumber?.toString()
                }
                errorText={
                  errorText
                    ? errorText
                    : visitorInfo?.guideChannel === "EMAIL"
                      ? "이메일 형식이 맞지 않습니다."
                      : "전화번호 형식이 맞지 않습니다."
                }
                onChange={(value) => {
                  setErrorText(undefined);
                  if (visitorInfo?.guideChannel === "KAKAO") setVisitorInfo({ ...visitorInfo, phoneNumber: value });
                  else setVisitorInfo({ ...visitorInfo, email: value });
                }}
                type={loading ? "disabled" : !isRightContact() || errorText ? "error" : "default"}
                styles={{
                  container: css`
                    width: 200px;
                  `,
                  input: css`
                    font-size: 15px;
                    height: 40px;
                    padding: 0px 18px;
                  `,
                }}
              />
            </Row>
          </div>
          {!loading && (
            <Button
              onClick={async () => {
                setLoading(true);

                const res = await reSendGuideMessage(visitorInfo);
                if (res.success) {
                  setLoading(false);
                  onUpdateVisitorInfo?.();
                  if (res.data?.isGuideMessageSent) {
                    closeModal();
                    onHandleSend?.();
                  } else {
                    setErrorText(res.data?.guideMessageErrorCode);
                  }
                } else {
                  setErrorText(res.message);
                }
              }}
              title="전송"
              styles={{
                container: css`
                  flex: 1;
                  padding: 16px 0;
                `,
              }}
            />
          )}
          {loading && (
            <LoadingBtn>
              <div
                css={css`
                  display: flex;
                  @keyframes lds-dual-ring {
                    0% {
                      transform: rotate(0deg);
                    }
                    100% {
                      transform: rotate(360deg);
                    }
                  }
                  animation: lds-dual-ring 1.2s linear infinite;
                `}
              >
                <Icon name="change" color={theme.neutral200} />
              </div>
            </LoadingBtn>
          )}
        </div>
      </Modal>
    );
  } else return <></>;
};

export default ReSendMessageModal;
